import { fetchCsvExport } from '@/lib/utils/exports'
import { populationSearchSchema, type PopulationDateKey } from '@/lib/utils/populationFilters'
import { useSearchedOptions, type SearchedOption } from '@/hooks/useSearchedOptions'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

/** The population filters (shared with New Mass Review) plus the list's paging and sort. */
export const customersSearchSchema = populationSearchSchema.extend({
  page: z.number().optional(),
  pageSize: z.number().optional(),
  sort: z
    .object({
      id: z.string(),
      desc: z.boolean(),
    })
    .optional(),
})

export type CustomersSearch = z.infer<typeof customersSearchSchema>

type CustomersQueries = Omit<CustomersSearch, PopulationDateKey> &
  Partial<Record<PopulationDateKey, string>>

async function getCustomers(queries: CustomersQueries, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers.$get(
    { query: stringifyValues(queries) },
    { init: { signal } },
  )

  return await res.json()
}

export type CustomerRow = Awaited<ReturnType<typeof getCustomers>>['results'][number]

const toDateOnly = (date: Date | undefined) => (date ? formatDateOnly(date) : undefined)

// The date ranges travel as 'YYYY-MM-DD' (the backend compares date columns).
function normalizeCustomersQueries(queries: CustomersSearch): CustomersQueries {
  return {
    ...queries,
    nextReviewDateFrom: toDateOnly(queries.nextReviewDateFrom),
    nextReviewDateTo: toDateOnly(queries.nextReviewDateTo),
    createdFrom: toDateOnly(queries.createdFrom),
    createdTo: toDateOnly(queries.createdTo),
    lastFailedPaymentFrom: toDateOnly(queries.lastFailedPaymentFrom),
    lastFailedPaymentTo: toDateOnly(queries.lastFailedPaymentTo),
  }
}

export function useCustomers(queries: CustomersSearch) {
  const normalized = normalizeCustomersQueries(queries)

  return useQuery({
    queryKey: ['customers', normalized],
    queryFn: ({ signal }) => getCustomers(normalized, signal),
  })
}

/**
 * CSV of every customer matching the given filters and sort (no pagination), every column included.
 * Pass the same search the list uses so the file matches what the table shows.
 */
export function exportCustomersCsv(queries: CustomersSearch, signal?: AbortSignal) {
  const { page: _page, pageSize: _pageSize, ...filters } = normalizeCustomersQueries(queries)
  const stringified = stringifyValues(filters)
  const defined = Object.fromEntries(
    Object.entries(stringified).filter(([, value]) => value !== undefined),
  )
  const params = new URLSearchParams(defined).toString()

  return fetchCsvExport(`/credit/customers/export?${params}`, signal)
}

/**
 * `asOf` rewinds the event-dated half of the payload — exposure, sales trend, order and payment
 * metrics, CI scores, the Equifax code, security deposits — to a past instant. The inbox passes a
 * resolved CLR's `resolvedAt` so an approver reopening their decision sees the evidence it was made
 * on. Left out everywhere else, which is the live read.
 */
function asOfQuery(asOf: Date | undefined) {
  return asOf ? { asOf: asOf.toISOString() } : {}
}

async function getCustomerDetail(erp: string, asOf?: Date, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers[':erp'].$get(
    { param: { erp }, query: asOfQuery(asOf) },
    { init: { signal } },
  )

  if (!res.ok) {
    throw new Error('Failed to load customer detail')
  }

  return await res.json()
}

export type CustomerDetail = Awaited<ReturnType<typeof getCustomerDetail>>
export type CustomerScorePoint = CustomerDetail['scoreHistory'][number]

/**
 * Query options so the route loader can prefetch the detail (for the breadcrumb) and the page reuses it.
 *
 * `asOf` is part of the key, not just the request. Without it the frozen payload for a resolved CLR
 * and the live one for the customer page would share a cache entry, and whichever loaded first would
 * be served to the other.
 */
export function customerDetailQueryOptions(erp: string, asOf?: Date) {
  return {
    queryKey: ['customerDetail', erp, asOf?.toISOString() ?? null],
    queryFn: ({ signal }: { signal: AbortSignal }) => getCustomerDetail(erp, asOf, signal),
  }
}

export function useCustomerDetail(erp: string, asOf?: Date) {
  return useQuery(customerDetailQueryOptions(erp, asOf))
}

/** The header Export button: the whole customer-detail page, not the rows behind one card. */
export function exportCustomerDetail(erp: string, signal?: AbortSignal) {
  return fetchCsvExport(`/credit/customers/${erp}/export`, signal)
}

export function exportCustomerOrderInformation(erp: string, asOf?: Date, signal?: AbortSignal) {
  return fetchCsvExport(
    `/credit/customers/${erp}/order-information/export${asOfSearch(asOf)}`,
    signal,
  )
}

/** `asOf` as a leading query string, or nothing at all on the live path. */
function asOfSearch(asOf: Date | undefined, separator: '?' | '&' = '?') {
  return asOf ? `${separator}asOf=${encodeURIComponent(asOf.toISOString())}` : ''
}

/**
 * The Credit Exposure chart's daily series. `years` is the charted window — 1 for the detail card,
 * 3 for the expanded dialog — so the file matches the curve the caller is looking at.
 */
export function exportCustomerCreditExposure(
  erp: string,
  years: 1 | 3,
  asOf?: Date,
  signal?: AbortSignal,
) {
  return fetchCsvExport(
    `/credit/customers/${erp}/credit-exposure/export?years=${years}${asOfSearch(asOf, '&')}`,
    signal,
  )
}

export function exportCustomerPaymentInformation(erp: string, asOf?: Date, signal?: AbortSignal) {
  return fetchCsvExport(
    `/credit/customers/${erp}/payment-information/export${asOfSearch(asOf)}`,
    signal,
  )
}

/**
 * The line items behind the Sales Trend chart: the FBL5N extract filtered to this customer's payer
 * column and nothing else — full layout, no date window, no document-type filter.
 */
export function exportCustomerTransactions(erp: string, asOf?: Date, signal?: AbortSignal) {
  return fetchCsvExport(
    `/credit/customers/${erp}/customer-transactions/export${asOfSearch(asOf)}`,
    signal,
  )
}

export function exportCustomerActiveSites(erp: string, signal?: AbortSignal) {
  return fetchCsvExport(`/credit/customers/${erp}/active-sites/export`, signal)
}

async function getCustomerCreditExposure(erp: string, asOf?: Date, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers[':erp']['credit-exposure'].$get(
    { param: { erp }, query: asOfQuery(asOf) },
    { init: { signal } },
  )

  if (!res.ok) {
    throw new Error('Failed to load credit exposure')
  }

  return await res.json()
}

export type CustomerCreditExposure = Awaited<ReturnType<typeof getCustomerCreditExposure>>

/** The wider 3-year exposure for the expandable dialog; fetched lazily (only when `enabled`). */
export function useCustomerCreditExposure(erp: string, enabled: boolean, asOf?: Date) {
  return useQuery({
    // Keyed on `asOf` for the same reason as the detail: the dialog's three-year curve and the
    // card's one-year curve must not be able to disagree about which instant they end at.
    queryKey: ['customerCreditExposure', erp, asOf?.toISOString() ?? null],
    queryFn: ({ signal }) => getCustomerCreditExposure(erp, asOf, signal),
    enabled,
  })
}

async function getCustomerActiveSites(erp: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers[':erp']['active-sites'].$get(
    { param: { erp } },
    { init: { signal } },
  )

  if (!res.ok) {
    throw new Error('Failed to load active sites')
  }

  const { results } = await res.json()
  return results
}

export type CustomerActiveSite = Awaited<ReturnType<typeof getCustomerActiveSites>>[number]

/** The sites behind the Active Sites count; fetched lazily (only when `enabled`). */
export function useCustomerActiveSites(erp: string, enabled: boolean) {
  return useQuery({
    queryKey: ['customerActiveSites', erp],
    queryFn: ({ signal }) => getCustomerActiveSites(erp, signal),
    enabled,
  })
}

export type OverexposureScope = 'bau' | 'all'

async function getCustomerOverexposure(erp: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers[':erp'].overexposure.$get(
    { param: { erp } },
    { init: { signal } },
  )

  if (!res.ok) {
    throw new Error('Failed to load overexposure')
  }

  return await res.json()
}

export type CustomerOverexposure = Awaited<ReturnType<typeof getCustomerOverexposure>>
export type CustomerOverexposureInvoice = CustomerOverexposure['bau']['invoices'][number]

/** The invoices behind the Overexposure Count; fetched lazily (only when `enabled`). */
export function useCustomerOverexposure(erp: string, enabled: boolean) {
  return useQuery({
    queryKey: ['customerOverexposure', erp],
    queryFn: ({ signal }) => getCustomerOverexposure(erp, signal),
    enabled,
  })
}

/** One scope's invoices, so the file matches the section the icon sits in. */
export function exportCustomerOverexposure(
  erp: string,
  scope: OverexposureScope,
  signal?: AbortSignal,
) {
  return fetchCsvExport(`/credit/customers/${erp}/overexposure/export?scope=${scope}`, signal)
}

async function getCustomerLegalNames(search: string, offset: number, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers['legal-names'].$get(
    { query: { search, offset: String(offset) } },
    { init: { signal } },
  )

  const { legalNames, nextOffset } = await res.json()
  return { options: legalNames.map((name) => ({ value: name, label: name })), nextOffset }
}

/** Legal name options for a dropdown searched as the user types; `selected` always offered. */
export function useCustomerLegalNameOptions(selected: string[]) {
  return useSearchedOptions({
    queryKey: 'customerLegalNames',
    selected,
    fetchMatches: getCustomerLegalNames,
  })
}

async function getCustomerAccountGroups(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers['account-groups'].$get({}, { init: { signal } })

  const { accountGroups } = await res.json()
  return accountGroups
}

export function useCustomerAccountGroups() {
  return useQuery({
    queryKey: ['customerAccountGroups'],
    queryFn: ({ signal }) => getCustomerAccountGroups(signal),
  })
}

async function getCustomerPaymentTerms(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit.customers['payment-terms'].$get({}, { init: { signal } })

  const { paymentTerms } = await res.json()
  return paymentTerms
}

export function useCustomerPaymentTerms() {
  return useQuery({
    queryKey: ['customerPaymentTerms'],
    queryFn: ({ signal }) => getCustomerPaymentTerms(signal),
  })
}

type CustomerErpsQuery = { search?: string; offset?: number; selected?: string[] }

/** "erp - business name", searchable by either half. */
const toErpOption = (row: { erp: string; businessName: string | null }): SearchedOption => ({
  value: row.erp,
  label: row.businessName ? `${row.erp} - ${row.businessName}` : row.erp,
  keywords: row.businessName ? [row.erp, row.businessName] : [row.erp],
})

async function getCustomerErps(
  { search, offset, selected }: CustomerErpsQuery,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit.customers.erps.$get(
    {
      query: {
        search,
        offset: offset === undefined ? undefined : String(offset),
        selected: selected ? JSON.stringify(selected) : undefined,
      },
    },
    { init: { signal } },
  )

  const { erps, nextOffset } = await res.json()
  return { options: erps.map(toErpOption), nextOffset }
}

/**
 * ERP options for a dropdown searched as the user types, labeled "erp - business name". The
 * `selected` ERPs are always offered, their names fetched if no search has shown them yet.
 */
export function useCustomerErpOptions(selected: string[]) {
  return useSearchedOptions({
    queryKey: 'customerErps',
    selected,
    fetchMatches: (search, offset, signal) => getCustomerErps({ search, offset }, signal),
    fetchSelected: async (values, signal) =>
      (await getCustomerErps({ selected: values }, signal)).options,
  })
}
